import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { BACKEND_URL } from '../config';

function BluetoothPrinterScanner({ theme }) {
    const { t } = useLanguage();
    const [scanning, setScanning] = useState(false);
    const [discoveredPrinters, setDiscoveredPrinters] = useState([]);
    const [savedPrinters, setSavedPrinters] = useState([]);
    const [editingPrinter, setEditingPrinter] = useState(null);
    const [customName, setCustomName] = useState('');
    const [saveStatus, setSaveStatus] = useState('');

    useEffect(() => {
        loadSavedPrinters();
    }, []);

    const loadSavedPrinters = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/bluetooth-printers`);
            if (response.ok) {
                const data = await response.json();
                setSavedPrinters(data);
            }
        } catch (error) {
            console.error('Error loading saved printers:', error);
        }
    };

    const scanForPrinters = async () => {
        if (!('bluetooth' in navigator)) {
            alert(t('bluetoothNotSupported') || 'Web Bluetooth API not supported');
            return;
        }

        setScanning(true);
        setDiscoveredPrinters([]);

        try {
            // Request Bluetooth device with ESC/POS service filter
            const device = await navigator.bluetooth.requestDevice({
                filters: [
                    { services: ['000018f0-0000-1000-8000-00805f9b34fb'] }, // ESC/POS service
                ],
                optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb'],
                acceptAllDevices: false
            });

            // Get device info
            const printerInfo = {
                name: device.name || 'Unknown Printer',
                id: device.id,
                // Note: Web Bluetooth API doesn't expose MAC address directly
                // We'll use device ID as identifier
                mac_address: device.id,
                connected: device.gatt?.connected || false
            };

            // Check if already in discovered list
            if (!discoveredPrinters.find(p => p.id === printerInfo.id)) {
                setDiscoveredPrinters(prev => [...prev, printerInfo]);
            }

        } catch (error) {
            if (error.name !== 'NotFoundError') {
                console.error('Error scanning for printers:', error);
                alert(t('errorScanningPrinters') || 'Error scanning for printers: ' + error.message);
            }
        } finally {
            setScanning(false);
        }
    };

    const handleSavePrinter = async (printer) => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/bluetooth-printers`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    mac_address: printer.mac_address,
                    device_name: printer.name,
                    custom_name: customName || printer.name,
                    device_type: 'thermal_printer'
                })
            });

            if (response.ok) {
                setSaveStatus(t('printerSaved') || 'Printer saved successfully!');
                setEditingPrinter(null);
                setCustomName('');
                await loadSavedPrinters();
            } else {
                const error = await response.json();
                setSaveStatus(t('error') + ': ' + (error.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error saving printer:', error);
            setSaveStatus(t('error') + ': ' + error.message);
        }

        setTimeout(() => setSaveStatus(''), 3000);
    };

    const handleDeletePrinter = async (mac_address) => {
        if (!window.confirm(t('confirmDeletePrinter') || 'Delete this printer?')) {
            return;
        }

        try {
            const response = await fetch(`${BACKEND_URL}/api/bluetooth-printers/${encodeURIComponent(mac_address)}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                setSaveStatus(t('printerDeleted') || 'Printer deleted successfully');
                await loadSavedPrinters();
            } else {
                setSaveStatus(t('error') + ': Failed to delete printer');
            }
        } catch (error) {
            console.error('Error deleting printer:', error);
            setSaveStatus(t('error') + ': ' + error.message);
        }

        setTimeout(() => setSaveStatus(''), 3000);
    };

    const sectionStyle = {
        marginBottom: '1.5rem',
        padding: '1.5rem',
        backgroundColor: theme?.colors?.background || '#f5f5f5',
        borderRadius: theme?.borderRadius || 8,
        border: `1px solid ${theme?.colors?.border || '#ddd'}`
    };

    const buttonStyle = {
        padding: '0.75rem 1.5rem',
        backgroundColor: theme?.colors?.primary || '#1976d2',
        color: theme?.colors?.surface || '#fff',
        border: 'none',
        borderRadius: theme?.borderRadius || 6,
        cursor: 'pointer',
        fontWeight: 'bold',
        fontSize: '1rem',
        marginRight: '0.5rem',
        marginBottom: '0.5rem'
    };

    const printerCardStyle = {
        padding: '1rem',
        backgroundColor: theme?.colors?.surface || '#fff',
        borderRadius: theme?.borderRadius || 8,
        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
        marginBottom: '1rem'
    };

    return (
        <div style={sectionStyle}>
            <h3 style={{ marginTop: 0, color: theme?.colors?.text }}>
                📱 {t('bluetoothPrinterDiscovery') || 'Bluetooth Printer Discovery'}
            </h3>

            <button
                onClick={scanForPrinters}
                disabled={scanning}
                style={{
                    ...buttonStyle,
                    opacity: scanning ? 0.6 : 1,
                    cursor: scanning ? 'not-allowed' : 'pointer'
                }}
            >
                {scanning ? (t('scanning') || 'Scanning...') : (t('scanForPrinters') || '🔍 Scan for Printers')}
            </button>

            {saveStatus && (
                <div style={{
                    padding: '0.75rem',
                    marginTop: '1rem',
                    backgroundColor: saveStatus.includes(t('error')) ? (theme?.colors?.error || '#d32f2f') : (theme?.colors?.success || '#2e7d32'),
                    color: '#fff',
                    borderRadius: theme?.borderRadius || 4,
                    fontWeight: 'bold'
                }}>
                    {saveStatus}
                </div>
            )}

            {/* Discovered Printers */}
            {discoveredPrinters.length > 0 && (
                <div style={{ marginTop: '1.5rem' }}>
                    <h4 style={{ color: theme?.colors?.text }}>{t('discoveredPrinters') || 'Discovered Printers'}</h4>
                    {discoveredPrinters.map((printer, index) => (
                        <div key={index} style={printerCardStyle}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                    <strong style={{ color: theme?.colors?.primary }}>{printer.name}</strong>
                                    <div style={{ color: theme?.colors?.textSecondary, fontSize: '0.9rem' }}>
                                        ID: {printer.id}
                                    </div>
                                </div>

                                {editingPrinter === printer.id ? (
                                    <div style={{ flex: 1, display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                        <input
                                            type="text"
                                            placeholder={t('enterCustomName') || 'Enter custom name...'}
                                            value={customName}
                                            onChange={(e) => setCustomName(e.target.value)}
                                            style={{
                                                flex: 1,
                                                padding: '0.5rem',
                                                borderRadius: theme?.borderRadius || 4,
                                                border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                                                backgroundColor: theme?.colors?.surface || '#fff',
                                                color: theme?.colors?.text || '#000'
                                            }}
                                        />
                                        <button
                                            onClick={() => handleSavePrinter(printer)}
                                            style={{
                                                ...buttonStyle,
                                                margin: 0,
                                                backgroundColor: theme?.colors?.success || '#2e7d32'
                                            }}
                                        >
                                            ✅ {t('save')}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setEditingPrinter(null);
                                                setCustomName('');
                                            }}
                                            style={{
                                                ...buttonStyle,
                                                margin: 0,
                                                backgroundColor: theme?.colors?.textSecondary || '#666'
                                            }}
                                        >
                                            ❌ {t('cancel')}
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => {
                                            setEditingPrinter(printer.id);
                                            setCustomName(printer.name);
                                        }}
                                        style={{
                                            ...buttonStyle,
                                            margin: 0
                                        }}
                                    >
                                        ✏️ {t('setCustomName') || 'Set Custom Name'}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Saved Printers */}
            {savedPrinters.length > 0 && (
                <div style={{ marginTop: '1.5rem' }}>
                    <h4 style={{ color: theme?.colors?.text }}>{t('savedPrinters') || 'Saved Printers'}</h4>
                    {savedPrinters.map((printer) => (
                        <div key={printer.id} style={printerCardStyle}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <strong style={{ color: theme?.colors?.primary, fontSize: '1.1rem' }}>
                                        {printer.custom_name}
                                    </strong>
                                    <div style={{ color: theme?.colors?.textSecondary, fontSize: '0.9rem' }}>
                                        {t('originalName')}: {printer.device_name}
                                    </div>
                                    <div style={{ color: theme?.colors?.textSecondary, fontSize: '0.85rem' }}>
                                        {t('lastSeen')}: {new Date(printer.last_seen).toLocaleString()}
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleDeletePrinter(printer.mac_address)}
                                    style={{
                                        ...buttonStyle,
                                        margin: 0,
                                        backgroundColor: theme?.colors?.error || '#d32f2f'
                                    }}
                                >
                                    🗑️ {t('delete')}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {savedPrinters.length === 0 && discoveredPrinters.length === 0 && !scanning && (
                <div style={{
                    marginTop: '1rem',
                    padding: '2rem',
                    textAlign: 'center',
                    color: theme?.colors?.textSecondary,
                    backgroundColor: theme?.colors?.surface || '#fff',
                    borderRadius: theme?.borderRadius || 8,
                    border: `2px dashed ${theme?.colors?.border || '#ddd'}`
                }}>
                    <p>{t('noPrintersFound') || 'No printers found. Click "Scan for Printers" to discover nearby Bluetooth thermal printers.'}</p>
                </div>
            )}
        </div>
    );
}

export default BluetoothPrinterScanner;
